/**
 * Duelos: buscar rival, jugar y enviar el resultado.
 *
 * Vive junto al tablero y no en una sección propia porque un duelo *es* una
 * partida: lo único que cambia es contra qué mazo se juega y que al terminar hay
 * que enviarla. Separarlo obligaría a duplicar el tablero entero.
 *
 * El envío es automático al terminar. Pedir al jugador que pulse "Enviar" abre
 * la puerta a que cierre la pestaña antes y pierda la recompensa de una partida
 * que sí jugó.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Swords, TriangleAlert, Trophy, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import {
  useFindOpponent,
  useMyPvpStatus,
  usePublishDeck,
  useSubmitMatch,
  type MatchOutcome,
} from '@/hooks/usePvp';
import { useGameStore } from '@/store/useGameStore';
import type { DeckEntry } from '@/engine/combatEngine';

interface PvpPanelProps {
  userId: string | undefined;
  deckMasterIds: readonly DeckEntry[];
  isOffline: boolean;
}

export function PvpPanel({ userId, deckMasterIds, isOffline }: PvpPanelProps) {
  const game = useGameStore((state) => state.game);
  const opponent = useGameStore((state) => state.pvpOpponent);
  const seed = useGameStore((state) => state.pvpSeed);
  const moves = useGameStore((state) => state.moves);
  const newMatch = useGameStore((state) => state.newMatch);

  const status = useMyPvpStatus(userId);
  const publish = usePublishDeck(userId);
  const find = useFindOpponent();
  const submit = useSubmitMatch(userId);

  const [outcome, setOutcome] = useState<MatchOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentMatchKey, setSentMatchKey] = useState<string | null>(null);

  const finished = game?.phase === 'FINISHED';
  const matchKey = opponent && seed !== null ? `${opponent.userId}:${seed}` : null;

  // Envío automático, una sola vez por partida. La clave incluye la semilla, así
  // que dos duelos seguidos contra el mismo rival no se confunden entre sí.
  useEffect(() => {
    if (!finished || !opponent || seed === null || !matchKey) return;
    if (sentMatchKey === matchKey || submit.isPending) return;

    setSentMatchKey(matchKey);
    setError(null);
    submit.mutate(
      { defenderId: opponent.userId, seed, moves },
      {
        onSuccess: (result) => setOutcome(result),
        onError: (submitError) => setError(submitError.message),
      },
    );
  }, [finished, opponent, seed, moves, matchKey, sentMatchKey, submit]);

  if (isOffline) return null;

  const startDuel = () => {
    setError(null);
    setOutcome(null);
    setSentMatchKey(null);
    find.mutate(undefined, {
      onSuccess: (rival) => newMatch({ deckIds: deckMasterIds, opponent: rival }),
      onError: (findError) => setError(findError.message),
    });
  };

  const busy = find.isPending || publish.isPending || submit.isPending;

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-center gap-2 px-2 pb-1">
      {!status.data ? (
        <>
          <span className="text-[11px] text-slate-400">
            Publica tu mazo para que otros puedan retarte.
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setError(null);
              publish.mutate(undefined, {
                onError: (publishError) => setError(publishError.message),
              });
            }}
          >
            <Upload className="h-3 w-3" aria-hidden />
            Publicar mazo
          </Button>
        </>
      ) : (
        <>
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
            <Trophy className="h-3 w-3 text-amber-300" aria-hidden />
            {status.data.rating} pts
          </span>
          <Button size="sm" variant="secondary" disabled={busy} onClick={startDuel}>
            {find.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <Swords className="h-3 w-3" aria-hidden />
            )}
            {opponent ? 'Otro duelo' : 'Buscar rival'}
          </Button>
          {opponent ? (
            <span className="text-[11px] text-slate-400">
              Duelo contra <strong className="text-slate-200">{opponent.username}</strong>
            </span>
          ) : null}
        </>
      )}

      <AnimatePresence>
        {submit.isPending ? (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="inline-flex items-center gap-1 text-[11px] text-slate-400"
          >
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            Validando la partida en el servidor…
          </motion.span>
        ) : outcome ? (
          <motion.span
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-950/70 px-2 py-0.5 text-[11px] text-emerald-200 ring-1 ring-emerald-500/40"
          >
            +{outcome.goldAwarded} de oro
            {outcome.ratingChange !== 0
              ? ` · ${outcome.ratingChange > 0 ? '+' : ''}${outcome.ratingChange} pts`
              : ''}
            {outcome.goldCapped ? ' · tope diario alcanzado' : ''}
          </motion.span>
        ) : error ? (
          <motion.span
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            role="alert"
            className="inline-flex items-center gap-1 rounded-full bg-rose-950/70 px-2 py-0.5 text-[11px] text-rose-200 ring-1 ring-rose-500/40"
          >
            <TriangleAlert className="h-3 w-3" aria-hidden />
            {error}
          </motion.span>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
